import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Both tables ship in migration_020_journey.sql. Hooks degrade
// gracefully if the migration hasn't run yet — same pattern the
// Reports page uses, so the UI doesn't hard-fail when the schema
// is missing.

function isMissingTable(err) {
  if (!err) return false
  if (err.code === 'PGRST205') return true
  return /schema cache/i.test(err.message || '') || /does not exist/i.test(err.message || '')
}

export function useUrges({ since } = {}) {
  const { user } = useAuth()
  const [urges, setUrges] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tableMissing, setTableMissing] = useState(false)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    let query = supabase
      .from('recovery_urges')
      .select('*')
      .eq('user_id', user.id)
      .order('recorded_at', { ascending: false })
    if (since) query = query.gte('recorded_at', since)
    const { data, error: err } = await query
    if (err) {
      if (isMissingTable(err)) setTableMissing(true)
      else setError(err.message)
    } else {
      setUrges(data || [])
      setTableMissing(false)
    }
    initialLoadedRef.current = true
    setLoading(false)
  }, [user, since])

  useEffect(() => { fetch() }, [fetch])

  const log = async ({ intensity, trigger_text, response, notes, recorded_at }) => {
    if (!user) return null
    const { error: err } = await supabase.from('recovery_urges').insert({
      user_id: user.id,
      intensity: intensity ?? null,
      trigger_text: trigger_text || null,
      response,
      notes: notes || null,
      recorded_at: recorded_at || new Date().toISOString(),
    })
    if (err) { setError(err.message); return false }
    await fetch()
    return true
  }

  const remove = async (id) => {
    const { error: err } = await supabase.from('recovery_urges').delete().eq('id', id)
    if (err) { setError(err.message); return false }
    await fetch()
    return true
  }

  return { urges, loading, error, tableMissing, log, remove, refetch: fetch }
}

export function useCheckins({ since } = {}) {
  const { user } = useAuth()
  const [checkins, setCheckins] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tableMissing, setTableMissing] = useState(false)
  const initialLoadedRef = useRef(false)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    let query = supabase
      .from('recovery_checkins')
      .select('*')
      .eq('user_id', user.id)
      .order('date', { ascending: false })
    if (since) query = query.gte('date', since)
    const { data, error: err } = await query
    if (err) {
      if (isMissingTable(err)) setTableMissing(true)
      else setError(err.message)
    } else {
      setCheckins(data || [])
      setTableMissing(false)
    }
    initialLoadedRef.current = true
    setLoading(false)
  }, [user, since])

  useEffect(() => { fetch() }, [fetch])

  const today = new Date().toLocaleDateString('en-CA')
  const todayCheckin = useMemo(() => checkins.find((c) => c.date === today) || null, [checkins, today])

  const upsert = async ({ date, mood, energy, urges_today, notes }) => {
    if (!user) return false
    const { error: err } = await supabase
      .from('recovery_checkins')
      .upsert(
        {
          user_id: user.id,
          date: date || today,
          mood: mood ?? null,
          energy: energy ?? null,
          urges_today: urges_today ?? false,
          notes: notes || null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,date' },
      )
    if (err) { setError(err.message); return false }
    await fetch()
    return true
  }

  return { checkins, todayCheckin, loading, error, tableMissing, upsert, refetch: fetch }
}

// Pure derivation off urges + checkins for the dashboard. Memoised
// so a typed character in the urge form doesn't recompute everything.
export function useJourneyStats(urges, checkins) {
  return useMemo(() => {
    const now = Date.now()
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000

    const inRange = (u, fromMs) => {
      const t = u.recorded_at ? Date.parse(u.recorded_at) : 0
      return t >= fromMs
    }

    const rate = (arr) => {
      if (arr.length === 0) return null
      const resisted = arr.filter((u) => u.response === 'resisted').length
      return (resisted / arr.length) * 100
    }

    const urges7 = urges.filter((u) => inRange(u, sevenDaysAgo))
    const urges30 = urges.filter((u) => inRange(u, thirtyDaysAgo))

    // Days since last lapse (most recent urge with response='lapsed').
    const lastLapse = urges.find((u) => u.response === 'lapsed')
    const daysSinceLapse = lastLapse
      ? Math.floor((now - Date.parse(lastLapse.recorded_at)) / (24 * 60 * 60 * 1000))
      : null

    // Trigger frequency in the last 30 days.
    const triggerCounts = {}
    for (const u of urges30) {
      if (!u.trigger_text) continue
      const key = u.trigger_text.trim().toLowerCase()
      if (!key) continue
      triggerCounts[key] = (triggerCounts[key] || 0) + 1
    }
    const topTriggers = Object.entries(triggerCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([trigger, count]) => ({ trigger, count }))

    return {
      urges7Count: urges7.length,
      urges7Resisted: urges7.filter((u) => u.response === 'resisted').length,
      urges7Rate: rate(urges7),
      urges30Count: urges30.length,
      urges30Resisted: urges30.filter((u) => u.response === 'resisted').length,
      urges30Rate: rate(urges30),
      daysSinceLapse,
      topTriggers,
      checkinsCount: checkins.length,
    }
  }, [urges, checkins])
}
